import type { FeedbackCategory, FeedbackRole, FeedbackSurveyType, AdditionalPractice } from './store/enums'
import type { ExperienceIssues, ConnectionIssues } from './enums'

import { parseStoredJson, safeLocalStorage } from '@shared/lib/browser'

import { FeedbackStep } from './store/enums'
import { useFeedbackStore } from './store'

interface FeedbackLocalData {
  step: FeedbackStep
  surveyType: FeedbackSurveyType | null
  role: FeedbackRole | null
  rating: number | null
  classroomRating: number | null
  tutorTransitionRating: number | null
  detailedFeedback: string
  category: FeedbackCategory | null
  selectedIssues: (ExperienceIssues | ConnectionIssues)[]
  comment: string
  additionalPractice: AdditionalPractice | null
}

interface FeedbackLocalStorageReturn {
  restore: () => void
  startSync: () => void
  clear: () => void
}

export const restoreFeedbackFromStorage = (bookingId: number): void => {
  const storageKey = `feedback-${bookingId}`
  // Called from route middleware, outside any component scope: the imperative safe wrapper.
  const data = parseStoredJson<FeedbackLocalData>(safeLocalStorage.getItem(storageKey))
  if (!data) return

  {
    const feedbackStore = useFeedbackStore()

    if (data.surveyType) feedbackStore.setSurveyType(data.surveyType)
    if (data.role) feedbackStore.setRole(data.role)
    if (data.rating) feedbackStore.setRating(data.rating)
    if (data.classroomRating) feedbackStore.setClassroomRating(data.classroomRating)
    if (data.tutorTransitionRating) feedbackStore.setTutorTransitionRating(data.tutorTransitionRating)
    if (data.detailedFeedback) feedbackStore.setDetailedFeedback(data.detailedFeedback)
    if (data.category) feedbackStore.setCategory(data.category)

    if (data.selectedIssues?.length) {
      data.selectedIssues.forEach(issue => feedbackStore.toggleIssue(issue))
    }

    if (data.comment) feedbackStore.setComment(data.comment)

    if (data.additionalPractice !== null && data.additionalPractice !== undefined) {
      feedbackStore.setAdditionalPractice(data.additionalPractice)
    }

    if (data.step) feedbackStore.setStep(data.step)
  }
}

export const useFeedbackLocalStorage = (bookingId: number): FeedbackLocalStorageReturn => {
  const route = useRoute()
  const router = useRouter()
  const feedbackStore = useFeedbackStore()
  const storageKey = `feedback-${bookingId}`

  const save = (data: FeedbackLocalData): void => {
    safeLocalStorage.setItem(storageKey, JSON.stringify(data))
  }

  const restore = (): void => {
    const currentStep = feedbackStore.getStep

    if (route.query.step !== currentStep) {
      router.replace({ query: { ...route.query, step: currentStep } })
    }
  }

  const startSync = (): void => {
    watch(
      () => ({
        step: feedbackStore.getStep,
        surveyType: feedbackStore.getSurveyType,
        role: feedbackStore.getRole,
        rating: feedbackStore.getRating,
        classroomRating: feedbackStore.getClassroomRating,
        tutorTransitionRating: feedbackStore.getTutorTransitionRating,
        detailedFeedback: feedbackStore.getDetailedFeedback,
        category: feedbackStore.getCategory,
        selectedIssues: [...feedbackStore.getSelectedIssues],
        comment: feedbackStore.getComment,
        additionalPractice: feedbackStore.getAdditionalPractice,
      }),
      (data) => {
        save(data)
      },
      { deep: true },
    )
  }

  const clear = (): void => {
    safeLocalStorage.removeItem(storageKey)
  }

  watch(
    () => feedbackStore.getStep,
    (step) => {
      if (step === FeedbackStep.Done) {
        clear()
      }
    },
  )

  return { restore, startSync, clear }
}
